// copy.js: everything typographic. Splits headlines into masked words,
// shows/hides the copy beats from the chapter progress, drifts them a
// little with the scroll, computes where the world's subject should sit so
// the copy has room (a pure function of progress), and runs the HUD and the
// labels that ride on world anchors.
import { $, $$, clamp, smooth } from './util.js';
import { CHAPTERS, FILM_CHAPTERS } from './film.js';

export function splitWords(root = document) {
  for (const el of $$('[data-split], .gate-title', root)) {
    if (el.dataset.splitDone) continue;
    let i = 0;
    const walk = (node) => {
      for (const ch of Array.from(node.childNodes)) {
        if (ch.nodeType === 3) {
          const frag = document.createDocumentFragment();
          for (const part of ch.textContent.split(/(\s+)/)) {
            if (!part) continue;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); continue; }
            const w = document.createElement('span');
            w.className = 'w';
            const inner = document.createElement('span');
            inner.textContent = part;
            inner.style.setProperty('--i', i++);
            w.appendChild(inner);
            frag.appendChild(w);
          }
          ch.replaceWith(frag);
        } else if (ch.nodeType === 1 && ch.tagName !== 'BR') walk(ch);
      }
    };
    walk(el);
    el.dataset.splitDone = '1';
  }
}

// v5.1 SPECTRUM (LIGHT.md §3): each highlighted phrase (.hl) is ONE gradient from --acc to --acc2 in reading order, even
// across line breaks. Every word gets its slice: background-size = the phrase's run length, position = minus the word's offset.
// (A letter split can do the same per letter: its offset = the word's offset + its own offset in the word.)
// baseline of a word mask (.w) below its content top (= the line top): a zero-size probe placed in the .w itself, so the
// span's reveal transform and padding never enter the measurement; the .w's own relative offset cancels (idempotent)
function baseRel(w) {
  const i = document.createElement('i');
  i.style.cssText = 'display:inline-block;width:0;height:0;margin:0;padding:0;border:0;vertical-align:baseline';
  w.appendChild(i);
  const y = i.getBoundingClientRect().top - (w.getBoundingClientRect().top + (parseFloat(getComputedStyle(w).paddingTop) || 0));
  i.remove();
  return y;
}
export function sliceHl(root = document) {
  for (const hl of $$('.hl', root)) {
    const head = hl.closest('.display'), caps = head && $$('.w', head).find((w) => !hl.contains(w));
    const first = hl.querySelector('.w');
    if (caps && first) { const d = baseRel(first) - baseRel(caps); hl.style.setProperty('--hly', `${(-d).toFixed(2)}px`); }
    const words = $$('.w > span', hl);
    if (!words.length || hl.querySelector('.gt-l')) continue; // already split into letters (gravitytype.js paints per letter)
    const fs = parseFloat(getComputedStyle(hl).fontSize) || 16;
    const padX = 0.32 * fs, gap = 0.26 * fs; // matches .hl .w > span padding in site.css
    const adv = words.map((s) => Math.max(0, s.offsetWidth - padX));
    const total = adv.reduce((a, b) => a + b, 0) + gap * (words.length - 1);
    let x = 0;
    words.forEach((s, i) => {
      s.style.backgroundSize = `${(total + padX).toFixed(1)}px 100%`;
      s.style.backgroundPosition = `${(-x).toFixed(1)}px 0`; // image starts at the phrase's first padding edge
      x += adv[i] + gap;
    });
  }
}
function watchHl() {
  const run = () => { try { sliceHl(); } catch (e) { /* cosmetic only */ } };
  run();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(run, () => {});
  let t = 0;
  addEventListener('resize', () => { clearTimeout(t); t = setTimeout(run, 160); }, { passive: true });
}

// staggered reveal delays for the non-heading children of a block
function tagReveals(block, start = 0.42) {
  let d = start;
  for (const c of Array.from(block.children)) {
    if (/^H[12]$/.test(c.tagName)) continue;
    c.classList.add('reveal');
    if (c.classList.contains('eyebrow')) { c.style.setProperty('--d', '0.02s'); continue; }
    c.style.setProperty('--d', `${d.toFixed(2)}s`);
    d += 0.12;
  }
}

export class Copy {
  constructor() {
    splitWords();
    watchHl();
    this.chaps = $$('.chap').map((el) => ({
      el, cur: null,
      beats: $$('.beat', el).map((b) => {
        tagReveals(b);
        b.inert = true; // [O3] a beat that isn't shown can't take focus (Shift+Tab reached the hidden Yours form)
        // side the SUBJECT goes to: copy left -> subject right (+1), copy right -> left (-1), centre copy -> 0
        const side = b.classList.contains('pos-right') ? -1 : b.classList.contains('pos-center') ? 0 : 1;
        return { el: b, a: +b.dataset.in, b: +b.dataset.out, side, on: false, ty: 1e9 };
      }),
    }));
    for (const b of $$('.offer-head, .cta-row')) tagReveals(b, 0.5);
    $$('.tier').forEach((t, i) => { t.classList.add('reveal'); t.style.setProperty('--d', `${(0.55 + i * 0.1).toFixed(2)}s`); });
    this.segs = this.chaps.map((c) => {
      const segs = [];
      for (const bt of c.beats) {
        const s = { a: bt.a - 0.07, b: bt.b + 0.06, side: bt.side };
        const last = segs[segs.length - 1];
        if (last && last.side === s.side && s.a - last.b < 0.16) last.b = s.b; else segs.push(s);
      }
      return segs;
    });
    this.anyOn = false;
    this.shiftOut = { x: 0, y: 0, copy: 0 };
  }

  // x: +1 subject right, -1 left; y: +1 subject up (units, the caller scales them)
  shift(ch, p, phone) {
    const o = this.shiftOut;
    o.x = 0; o.y = 0; o.copy = 0;
    const segs = this.segs[ch];
    if (!segs) return o;
    for (const s of segs) {
      const e = smooth(s.a, s.a + 0.08, p) * (1 - smooth(s.b - 0.08, s.b, p));
      if (e <= 0) continue;
      o.copy = Math.max(o.copy, e);
      if (phone) o.y = Math.max(o.y, e);
      else { o.x += e * s.side; if (s.side === 0) o.y = Math.max(o.y, e * 0.45); }
    }
    return o;
  }

  // show the beats of film chapter `ch` at progress p (hidden while gated / in cuts / under the offer)
  update(ch, p, visible) {
    let any = false;
    for (let i = 0; i < this.chaps.length; i++) {
      const c = this.chaps[i];
      const cur = i === ch;
      if (c.cur !== cur) { c.el.classList.toggle('cur', cur); c.cur = cur; }
      for (const bt of c.beats) {
        const on = visible && cur && p >= bt.a && p <= bt.b;
        if (bt.on !== on) { bt.el.classList.toggle('on', on); bt.el.inert = !on; bt.on = on; }
        if (on) {
          any = true;
          const lp = (p - bt.a) / Math.max(1e-3, bt.b - bt.a);
          const ty = Math.round((0.5 - lp) * 34 * 4) / 4;
          if (ty !== bt.ty) { bt.el.style.transform = `translate3d(0, ${ty}px, 0)`; bt.ty = ty; }
        }
      }
    }
    this.anyOn = any;
    return any;
  }
}

export class Hud {
  constructor(film) {
    this.el = $('#hud');
    this.num = $('#hud-num');
    this.name = $('#hud-name');
    this.tag = $('#hud-tag');
    this.fill = $('#hud-fill');
    this.ch = -1;
    this.f = -1;
    this.timer = 0;
    this.layout(film);
  }
  layout(film) {
    const ticks = $$('.hud-ticks i');
    ticks.forEach((t, i) => {
      if (i < FILM_CHAPTERS) t.style.left = `${(film.starts[i] / film.total) * 100}%`;
      else t.style.left = '100%';
    });
  }
  set(ch, f) {
    if (ch !== this.ch) {
      const first = this.ch < 0;
      this.ch = ch;
      const apply = () => {
        this.num.textContent = String(ch + 1).padStart(2, '0');
        this.name.textContent = CHAPTERS[ch].name;
        this.tag.textContent = CHAPTERS[ch].tag;
        this.el.classList.remove('swap');
      };
      clearTimeout(this.timer);
      if (first) apply(); else { this.el.classList.add('swap'); this.timer = setTimeout(apply, 260); }
    }
    const q = Math.round(clamp(f, 0, 1) * 1000) / 1000;
    if (q !== this.f) { this.fill.style.transform = `scaleX(${q})`; this.f = q; }
  }
}

// DOM labels riding on world anchors ({x, y, visible}, 0..1 from the top-left)
export class Labels {
  constructor(root) { this.root = root; this.items = []; }
  add(spec) {
    const el = document.createElement('div');
    el.className = `label ${spec.cls || ''}`;
    el.innerHTML = '<i></i><span></span>';
    this.root.appendChild(el);
    const item = { ...spec, el, span: el.querySelector('span'), on: false, tx: '', text: '', row: /\bbrand\b/.test(spec.cls || ''), off: null };
    this.items.push(item);
    this.setText(item, spec.text, spec.sub);
    return item;
  }
  setText(item, text, sub) {
    const key = `${text}|${sub || ''}`;
    if (item.text === key) return;
    item.text = key;
    item.off = null; // [O1] new text, new wrap: re-measure the ring row
    item.span.textContent = text;
    if (sub) { const s = document.createElement('small'); s.textContent = sub; item.span.appendChild(s); }
  }
  // ctx: { slot, ch, p, world, w, h }
  update(ctx) {
    if (ctx.w !== this.w0) { this.w0 = ctx.w; for (const it of this.items) it.off = null; } // [O1] a resize can change the wrap
    for (const it of this.items) {
      let a = null;
      if (ctx.world && ctx.world.anchors && it.slot === ctx.slot && it.ch === ctx.ch && ctx.p >= it.p0 && ctx.p <= it.p1) {
        for (const n of it.anchors) {
          const q = ctx.world.anchors[n];
          if (q && q.visible !== false && Number.isFinite(q.x) && Number.isFinite(q.y) && q.x > 0.03 && q.x < 0.97 && q.y > 0.06 && q.y < 0.9) { a = q; break; }
        }
      }
      const on = !!a;
      if (on) {
        const flip = a.x > 0.66;
        if (flip !== !!it.flip) { it.el.classList.toggle('flip', flip); it.flip = flip; }
        // the ring's centre sits on the anchor: -50% centres the row vertically, flip labels end at the anchor (site.css .label i)
        // [O1] row labels (.label.brand): the ring's own row sits on the anchor (measured per text/width; a pure translate cancels).
        // Phones align the ring with the LAST row (site.css), so a long name that wraps grows upward, away from the Yours
        // headline below the star; desktop keeps the centred row (off = h/2, exactly the old -50%)
        if (it.row && it.off === null) { const r = it.el.getBoundingClientRect(), q = it.el.querySelector('i').getBoundingClientRect(); it.off = q.height > 0 ? q.top + q.height / 2 - r.top : 13; }
        const tx = `translate3d(${(a.x * ctx.w).toFixed(1)}px, ${(a.y * ctx.h).toFixed(1)}px, 0) translate(${flip ? '-100%' : '0'}, ${it.row ? `${(-it.off).toFixed(1)}px` : '-50%'})`;
        if (tx !== it.tx) { it.el.style.transform = tx; it.tx = tx; }
      }
      if (on !== it.on) { it.el.classList.toggle('on', on); it.on = on; }
    }
  }
}
